import React from 'react'
import {
  Box,
  Chip,
  Container,
  Grid,
  Paper,
  styled,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  useMediaQuery,
} from '@mui/material'
import lightTheme from '../../styles/theme/lightTheme'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faFilePlus } from '@fortawesome/pro-regular-svg-icons'
import '../../styles/components/_box.scss'
import lock from '../../assets/images/lock.png'
import { CustomButton, GradientButton } from '../buttons'
import moment from 'moment-timezone'
import { niveauCompetencesList } from '../../data'

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  height: 435,
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  rowGap: '20px',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
    '&::before': {
      opacity: 1,
    },
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ItemLong = styled(Paper)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  padding: '24px',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
  },
}))

const TitleBox = styled(Typography)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 16,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  textTransform: 'uppercase',
  position: 'relative',
  paddingBottom: '8px',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '40px',
    height: '3px',
    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '2px',
  },
}))

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const IconContainer = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  border: '2px solid rgba(102, 126, 234, 0.3)',
  borderRadius: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  backgroundColor: 'rgba(102, 126, 234, 0.05)',
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    border: '2px solid rgba(102, 126, 234, 0.5)',
    transform: 'scale(1.1)',
  },

  '&:active': {
    transform: 'scale(0.95)',
  },
}))

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(99, 102, 241, 0.08)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  color: '#6366f1',
  fontWeight: 600,
  fontSize: '0.875rem',
  letterSpacing: '0.5px',
  '&:hover': {
    backgroundColor: 'rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const AnnexSkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.2)',
  color: '#f59e0b',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const SoftSkillChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(245, 158, 11, 0.08)',
  border: '1px solid rgba(245, 158, 11, 0.2)',
  color: '#f59e0b',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(245, 158, 11, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const ValueChip = styled(Chip)(({ theme }) => ({
  margin: '4px',
  backgroundColor: 'rgba(139, 92, 246, 0.08)',
  border: '1px solid rgba(139, 92, 246, 0.2)',
  color: '#8b5cf6',
  fontWeight: 500,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    transform: 'translateY(-1px)',
  },
}))

const WrapperIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  columnGap: '8px',
}))

const gradientButtonSx = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 'bold',
  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
    transform: 'translateY(-2px)',
  },
}

const MasonryLayout = ({
  profil,
  besoinClient,
  resumeClient,
  commentaireAdmin,
  rendezVous,
  offre,
  ged,
  relation,
  valeurs,
  competences,
  competencesAnnexes,
  softskils,
  type,
  displayValeurSection,
  displayCompetencesSection,
  displaySoftskilSection,
  handleOpenDoc,
  handleOpenBesoin,
  handleOpenCommentaire,
  handleOpenResume,
  handleOpenCompetences,
  handleOpenCompetencesAnnexes,
  handleOpenValeurs,
  handleOpenSoftskils,
  handleOpenOffer,
  displayParams = true,
  feedback,
}) => {
  const sizeChip = useMediaQuery(lightTheme.breakpoints.down('sm'))
    ? 'small'
    : 'medium'

  const profileBannerGradient =
    type === 'entreprise'
      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'

  const profileBannerShadow =
    type === 'entreprise'
      ? '0 8px 32px rgba(102, 126, 234, 0.25)'
      : '0 8px 32px rgba(240, 147, 251, 0.25)'

  const profileBannerTitle =
    type === 'entreprise' ? 'Espace Entreprise' : 'Espace Candidat'

  return (
    <Container
      maxWidth={'xl'}
      sx={{
        paddingTop: { xs: '16px', sm: '25px' },
        paddingBottom: { xs: '16px', sm: '25px' },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Grid container spacing={{ xs: 2, sm: 3, lg: 4 }}>
        {/* Section profil */}
        <Grid item xs={12} sm={6} lg={6} order={1}>
          <Item
            sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              padding: { xs: 0, lg: 3 },
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '60px',
                background: profileBannerGradient,
                display: { xs: 'none', lg: 'flex' },
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '16px 16px 0 0',
                boxShadow: profileBannerShadow,
                zIndex: 1,
              }}
            >
              <TitleBox style={{ color: '#fff' }}>
                {profileBannerTitle}
              </TitleBox>
            </Box>

            <Box
              sx={{
                pt: { xs: 0, lg: '60px' },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {profil}
            </Box>
          </Item>
        </Grid>

        {/* Section RDV */}
        <Grid item xs={12} sm={6} lg={6} order={2} position={'relative'}>
          <Item sx={{ height: '100%' }}>
            <TitleContainer>
              <TitleBox>Rendez-vous</TitleBox>
            </TitleContainer>
            <Box>{rendezVous}</Box>
          </Item>
        </Grid>

        {/* Recherche candidat / Fiches de poste */}
        <Grid item xs={12} sm={6} lg={6} order={3}>
          <Item sx={{ height: '100%' }}>
            <TitleContainer>
              <TitleBox>
                {type === 'entreprise' ? (
                  <>Fiches de poste</>
                ) : (
                  <>Recherche candidat</>
                )}
              </TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenBesoin}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box width={'100%'} height={'100%'}>
              {besoinClient}
            </Box>
          </Item>
        </Grid>

        {/* Historique des achats */}
        <Grid item xs={12} sm={6} lg={6} order={4} position={'relative'}>
          <Item
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              rowGap: '20px',
              justifyContent: 'space-between',
            }}
          >
            <TitleContainer>
              <TitleBox>Historique des achats</TitleBox>
            </TitleContainer>
            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {offre}
            </Box>
            {displayParams && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  rowGap: '15px',
                }}
              >
                <GradientButton onClick={handleOpenOffer}>
                  Proposer une offre
                </GradientButton>
              </Box>
            )}
          </Item>
        </Grid>

        {/* Section GED */}
        <Grid item xs={12} sm={12} lg={12} order={5} position={'relative'}>
          <Item
            sx={{
              height: { sm: 400, lg: 435 },
            }}
            className={ged ? '' : 'blurLayout'}
          >
            <TitleContainer>
              <TitleBox>Documents</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenDoc}>
                    <FontAwesomeIcon
                      icon={faFilePlus}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box overflow={'auto'} width={'100%'} height={'100%'}>
              {ged}
            </Box>
          </Item>
          {!ged && (
            <img
              src={lock}
              alt={'Jobertech - not access'}
              style={{
                width: 150,
                height: 150,
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-40%, -45%)',
              }}
            />
          )}
        </Grid>

        {/* Section Feedbacks */}
        <Grid item xs={12} sm={12} order={6}>
          <Item sx={{ height: '100%' }}>
            <TitleContainer>
              <TitleBox>Feedbacks</TitleBox>
            </TitleContainer>
            <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
              <TableContainer>
                <Table>
                  <HeaderTab>
                    <TableRow>
                      <TitleHead>Entreprise</TitleHead>
                      <TitleHead>Date d'entretien</TitleHead>
                      <TitleHead>Feedback</TitleHead>
                    </TableRow>
                  </HeaderTab>
                  {feedback &&
                    feedback.map((rdv, i) => (
                      <BodyTable key={i}>
                        {rdv.feedbackEntreprise && (
                          <TableRow>
                            <StyledTableCell>
                              {rdv.entreprise.raisonSocial}
                            </StyledTableCell>
                            <StyledTableCell>
                              {moment(rdv.dateDebut)
                                .tz('Europe/Paris')
                                .format('DD/MM/YYYY HH:mm')}
                            </StyledTableCell>
                            <StyledTableCell>
                              {rdv.feedbackEntreprise}
                            </StyledTableCell>
                          </TableRow>
                        )}
                      </BodyTable>
                    ))}
                </Table>
              </TableContainer>
            </Box>
          </Item>
        </Grid>

        {/* Commentaires jobertech */}
        <Grid item xs={12} sm={12} order={7}>
          <Item sx={{ height: '100%' }}>
            <TitleContainer>
              <TitleBox>Commentaires jobertech</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenCommentaire}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box>{commentaireAdmin}</Box>
          </Item>
        </Grid>

        {/* Résumé du candidat */}
        {type !== 'entreprise' && (
          <Grid item xs={12} sm={12} order={8}>
            <Item sx={{ height: '100%' }}>
              <TitleContainer>
                <TitleBox>Résumé du candidat</TitleBox>
                {displayParams && (
                  <WrapperIcons>
                    <IconContainer onClick={handleOpenResume}>
                      <FontAwesomeIcon
                        icon={faPen}
                        fontSize={15}
                        color={lightTheme.palette.primary.b2}
                      />
                    </IconContainer>
                  </WrapperIcons>
                )}
              </TitleContainer>
              <Box>{resumeClient}</Box>
            </Item>
          </Grid>
        )}

        {/* Compétences principales */}
        <Grid
          item
          xs={12}
          sm={12}
          order={9}
          display={displayCompetencesSection}
        >
          <ItemLong>
            <TitleContainer>
              <TitleBox>compétences techniques ou métiers principales</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenCompetences}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                py: 2,
              }}
            >
              {competences &&
                competences.map((competence, i) => (
                  <SkillChip
                    key={i}
                    size={sizeChip}
                    label={`${competence.competence} - ${
                      niveauCompetencesList[competence.niveau - 1].titre['fr']
                    }`}
                  />
                ))}
            </Box>
          </ItemLong>
        </Grid>

        {/* Compétences annexes */}
        <Grid
          item
          xs={12}
          sm={12}
          order={10}
          display={displayCompetencesSection}
        >
          <ItemLong>
            <TitleContainer>
              <TitleBox>Compétences techniques annexes</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenCompetencesAnnexes}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                py: 2,
              }}
            >
              {competencesAnnexes &&
                competencesAnnexes.map((competence, i) => (
                  <AnnexSkillChip
                    key={i}
                    size={sizeChip}
                    label={`${competence.competence} - ${
                      niveauCompetencesList[competence.niveau - 1].titre['fr']
                    }`}
                  />
                ))}
            </Box>
          </ItemLong>
        </Grid>

        {/* Compétences personnelles */}
        <Grid item xs={12} sm={12} order={11} display={displaySoftskilSection}>
          <ItemLong>
            <TitleContainer>
              <TitleBox>Compétences personnelles</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenSoftskils}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                py: 2,
              }}
            >
              {softskils &&
                softskils.map((softskil, i) => (
                  <SoftSkillChip key={i} label={softskil} size={sizeChip} />
                ))}
            </Box>
          </ItemLong>
        </Grid>

        {/* Valeurs */}
        <Grid item xs={12} sm={12} order={12} display={displayValeurSection}>
          <ItemLong>
            <TitleContainer>
              <TitleBox>Valeurs</TitleBox>
              {displayParams && (
                <WrapperIcons>
                  <IconContainer onClick={handleOpenValeurs}>
                    <FontAwesomeIcon
                      icon={faPen}
                      fontSize={15}
                      color={lightTheme.palette.primary.b2}
                    />
                  </IconContainer>
                </WrapperIcons>
              )}
            </TitleContainer>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                py: 2,
              }}
            >
              {valeurs &&
                valeurs.map((valeur, i) => (
                  <ValueChip key={i} label={valeur} size={sizeChip} />
                ))}
            </Box>
          </ItemLong>
        </Grid>
      </Grid>
    </Container>
  )
}

export default MasonryLayout
